import { z } from 'zod';
import { localeSchema } from './common.js';

/**
 * Marketing contracts (CLAUDE.md §2 `marketing`: landing + self-service
 * signup).
 *
 * Self-service signup is NOT a form — it is the Telegram Login Widget on the
 * admin app, which already exists. What lives here is the narrower thing the
 * landing page needs: a way for a merchant who is not ready to sign up today
 * to leave their number, so §8's direct pilot recruitment has a list to work.
 */

export const leadSchema = z.object({
  name: z.string().min(2).max(80),
  /** At least one of phone or Telegram must be present — see the refine below. */
  phone: z.string().max(24).optional(),
  telegramUsername: z.string().max(40).optional(),
  sells: z.string().max(80).optional(),
  monthlyOrders: z.string().max(40).optional(),
  note: z.string().max(1000).optional(),
  locale: localeSchema.default('km'),
  /** `?src=` from the landing URL, so we learn which channel recruits. */
  source: z.string().max(60).optional(),
  /**
   * Honeypot. A real merchant never sees or fills this; a scripted spam bot
   * fills every field it finds. Cheaper and less hostile than a CAPTCHA, which
   * on Cambodian mobile data would cost us real signups.
   *
   * The schema deliberately ACCEPTS any value here. Rejecting it at validation
   * would answer the bot with "the field named `company` is the problem",
   * which teaches it to skip the trap next time. The handler detects a filled
   * honeypot and returns a perfectly ordinary success instead.
   */
  company: z.string().max(200).optional(),
});
export type LeadInput = z.infer<typeof leadSchema>;

export const leadResponseSchema = z.object({
  received: z.boolean(),
});

/**
 * Pricing, exported so the landing page and the admin's plan picker cannot
 * disagree about what a plan costs. The numbers themselves live in
 * `billing.ts` — this is only the marketing framing around them.
 */
export const PLAN_PITCH = {
  free: {
    tagline: { km: 'ចាប់ផ្តើមឥតគិតថ្លៃ', en: 'Start for free' },
    bullets: {
      km: [
        'ហាងអនឡាញ + បូតផ្ទាល់ខ្លួន',
        'ទទួលប្រាក់តាម KHQR',
        'មុខងារ AI ទាំងអស់ ក្នុងកម្រិត credits',
        'ទំនិញរហូតដល់ ៣០ មុខ',
      ],
      en: [
        'Storefront + your own bot',
        'KHQR bank transfer',
        'Every AI feature, within your credits',
        'Up to 30 products',
      ],
    },
  },
  starter: {
    tagline: { km: 'សម្រាប់ហាងទើបចាប់ផ្តើម', en: 'For a shop just starting out' },
    bullets: {
      km: ['ហាងអនឡាញ + បូតផ្ទាល់ខ្លួន', 'ទទួលប្រាក់តាម KHQR', 'បង់ប្រាក់ពេលទទួលទំនិញ (COD)', 'នាំចូលទំនិញពី Facebook'],
      en: ['Storefront + your own bot', 'KHQR bank transfer', 'Cash on delivery', 'Import products from Facebook'],
    },
  },
  pro: {
    tagline: { km: 'សម្រាប់ហាងដែលមានការបញ្ជាទិញរាល់ថ្ងៃ', en: 'For a shop with orders every day' },
    bullets: {
      km: [
        'ផ្គូផ្គងប្រាក់ដោយស្វ័យប្រវត្តិពីក្រុម ABA',
        'រូបរាងហាងគ្រប់ស្ទីល + ប្តូរពណ៌',
        'កូដបញ្ចុះតម្លៃ និងការផ្សាយសារ',
        'credits ច្រើនជាង ៤ ដង',
      ],
      en: [
        'Automatic matching from your ABA group',
        'All storefront templates + colour',
        'Discount codes and broadcasts',
        'Four times the credits',
      ],
    },
  },
  business: {
    tagline: { km: 'សម្រាប់ហាងធំ ដែលចង់បានឆានែលបង់ប្រាក់ផ្លូវការ', en: 'For a larger shop that wants an official gateway' },
    bullets: {
      km: ['Bakong និង ABA PayWay', 'អ្នកចែកចាយ និងកម្រៃជើងសារ', 'បុគ្គលិកបានដល់ ១០ នាក់', 'របាយការណ៍លម្អិត'],
      en: ['Bakong and ABA PayWay', 'Affiliates and commissions', 'Up to 10 staff', 'Advanced reporting'],
    },
  },
} as const;
